/**
 * Read the Data sheet (xlsx) or a header row (csv) into load rows.
 * Excel date cells become YYYY-MM-DD. Blank / EXAMPLE rows stay in the
 * payload; the server ignores them.
 */

import ExcelJS from 'exceljs';

export interface ParsedLoadRow {
  kpi_name: string;
  period_from: string | null;
  period_to: string | null;
  value: string;
  use_type: string | null;
  business_name: string | null;
  functional_name: string | null;
  geographic_name: string | null;
  change_reason: string | null;
  notes: string | null;
  source_row: number;
}

function cellText(value: ExcelJS.CellValue): string {
  if (value == null) return '';
  if (value instanceof Date) return value.toISOString().slice(0, 10);
  if (typeof value === 'object') {
    const rich = value as { text?: string; result?: unknown; richText?: Array<{ text: string }> };
    if (rich.richText) return rich.richText.map((part) => part.text).join('');
    if (rich.text != null) return String(rich.text);
    if (rich.result != null) return cellText(rich.result as ExcelJS.CellValue);
  }
  return String(value).trim();
}

function emptyToNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

function recordToRow(record: Record<string, string>, sourceRow: number): ParsedLoadRow {
  return {
    kpi_name: record.kpi_name || record.name || '',
    period_from: emptyToNull(record.period_from || ''),
    period_to: emptyToNull(record.period_to || record.measurement_date || ''),
    value: record.value || '',
    use_type: emptyToNull(record.use_type || ''),
    business_name: emptyToNull(record.business_name || ''),
    functional_name: emptyToNull(record.functional_name || ''),
    geographic_name: emptyToNull(record.geographic_name || ''),
    change_reason: emptyToNull(record.change_reason || ''),
    notes: emptyToNull(record.notes || record.note || ''),
    source_row: sourceRow
  };
}

export async function parseLoadWorkbook(file: File): Promise<ParsedLoadRow[]> {
  if (file.name.toLowerCase().endsWith('.csv')) {
    const text = await file.text();
    const lines = text.split(/\r?\n/).filter((line) => line.trim());
    const headers = (lines[0] || '').split(',').map((header) => header.trim().toLowerCase());
    return lines.slice(1).map((line, index) => {
      const values = line.split(',').map((value) => value.trim());
      const record: Record<string, string> = {};
      headers.forEach((header, col) => {
        record[header] = values[col] || '';
      });
      return recordToRow(record, index + 2);
    });
  }

  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(await file.arrayBuffer());
  const sheet =
    workbook.worksheets.find((item) => item.name.toLowerCase() === 'data') || workbook.worksheets[0];
  if (!sheet) return [];

  const headerRow = sheet.getRow(1);
  const headers: string[] = [];
  headerRow.eachCell((cell, col) => {
    headers[col] = cellText(cell.value).toLowerCase();
  });

  const rows: ParsedLoadRow[] = [];
  sheet.eachRow((row, rowNumber) => {
    if (rowNumber === 1) return;
    const record: Record<string, string> = {};
    headers.forEach((header, col) => {
      if (!header) return;
      record[header] = cellText(row.getCell(col).value);
    });
    rows.push(recordToRow(record, rowNumber));
  });
  return rows;
}
